/**
 * Progreso por ejercicio y por músculo (§5.1, §5.2, §5.4). Lógica compartida.
 *
 * La selección se inicializa con el segmento de la URL: `/progreso/ej-chest-press` es el contrato
 * de navegación de flujo.md §5 y lo usa "Ver su progresión" desde el registro. Sin esto el enlace
 * abría la pantalla en el primer ejercicio de la lista, que casi nunca es el que se venía a mirar.
 */

import { useMemo, useState } from 'react';
import {
  E1RM_ADVERTENCIA,
  balanceEmpujeTiron,
  balanceSuperiorInferior,
  detectarEstancamiento,
  formatearNumero,
  frecuenciaPorMusculo,
  mapaCalor,
  nombreMusculo,
  progresionSemanal,
  recordsDeEjercicio,
  seriesEfectivas,
  tendenciaPorNivel,
  ultimaMarca,
  type Balance,
  type CeldaMapaCalor,
  type Ejercicio,
  type Estancamiento,
  type Musculo,
  type RecordsEjercicio,
  type SemanaEjercicio,
  type TendenciaNivel,
} from '@mg/core';

import type { EstadoPlaca } from '../ui/componentes.tsx';
import { useRuta } from '../ui/router.tsx';
import { useDatos } from './useDatos.ts';

/** Con menos de esto no hay forma que dibujar: son una o dos barras sueltas. Se dice el número. */
export const MIN_SEMANAS_GRAFICO = 3;

export interface FilaEjercicio {
  ejercicio: Ejercicio;
  records: RecordsEjercicio;
  progresion: SemanaEjercicio[];
  estancamiento: Estancamiento | null;
  sesiones: number;
  ultimaFecha: string | null;
  /** Variación de e1RM entre la primera y la última semana con datos, en kg. */
  deltaE1rm: number | null;
}

/**
 * "2026-W33" → "Semana 33". La clave ISO es cómo se agrupa por dentro, no cómo se lee: en una
 * pantalla con fechas en dd/mm, un "2026-W33" suelto es el único dato que hay que decodificar.
 */
export function nombreSemana(clave: string): string {
  const n = Number(clave.slice(6));
  return Number.isFinite(n) && n > 0 ? `Semana ${n}` : clave;
}

/** "Semana 33: 653 kg" — la lectura de una progresión demasiado corta para un gráfico. */
export function semanasEnTexto(progresion: SemanaEjercicio[]): string[] {
  return progresion.map((s) => `${nombreSemana(s.semana)}: ${formatearNumero(s.volumenKg, 0)} kg`);
}

/**
 * Placas ENTERAS para un balance.
 *
 * `estadosDeRatio` pinta media placa cuando la proporción cae entre dos, y media placa dentro de
 * una pila que promete unidades discretas se ve como una placa rota, no como "y medio". Acá el
 * conteo se redondea al entero más cercano: la pila es la forma, y los números exactos —que están
 * escritos al lado, "11 empuje · 9 tirón"— son el dato.
 */
export function placasDeBalance(balance: Balance, placas: number, tono: 'llena' | 'ambar'): EstadoPlaca[] {
  if (balance.proporcion === null || !Number.isFinite(balance.proporcion)) {
    return new Array<EstadoPlaca>(placas).fill('hueca');
  }
  const llenas = Math.min(placas, Math.max(0, Math.round(balance.proporcion * placas)));
  return Array.from({ length: placas }, (_, i) => (i < llenas ? tono : 'hueca'));
}

export function useProgreso() {
  const datos = useDatos();
  const { detalle: idEnLaRuta } = useRuta();

  // El id de la URL manda cada vez que cambia; después el usuario elige con el dedo o el mouse.
  const [elegido, setElegido] = useState<string | null>(idEnLaRuta);
  const [ultimaRuta, setUltimaRuta] = useState<string | null>(idEnLaRuta);
  if (idEnLaRuta !== ultimaRuta) {
    setUltimaRuta(idEnLaRuta);
    setElegido(idEnLaRuta);
  }

  const formula = datos.perfil?.formula_e1rm ?? 'epley';

  const filas = useMemo<FilaEjercicio[]>(() => {
    const conDatos = new Set(datos.contextos.filter((c) => c.serie.completada).map((c) => c.serie.ejercicio_id));

    return datos.ejercicios
      .filter((e) => conDatos.has(e.id))
      .map((ejercicio) => {
        const progresion = progresionSemanal(datos.contextos, ejercicio.id, formula);
        const primera = progresion[0];
        const ultima = progresion[progresion.length - 1];
        const marca = ultimaMarca(datos.contextos, ejercicio.id);

        return {
          ejercicio,
          records: recordsDeEjercicio(datos.contextos, ejercicio.id, formula),
          progresion,
          estancamiento: detectarEstancamiento(datos.contextos, ejercicio.id, 3),
          sesiones: new Set(
            datos.contextos
              .filter((c) => c.serie.ejercicio_id === ejercicio.id && c.serie.completada)
              .map((c) => c.sesion_id),
          ).size,
          ultimaFecha: marca?.fecha ?? null,
          deltaE1rm:
            primera?.e1rmMaxKg != null && ultima?.e1rmMaxKg != null && progresion.length > 1
              ? ultima.e1rmMaxKg - primera.e1rmMaxKg
              : null,
        } satisfies FilaEjercicio;
      })
      .sort((a, b) => (a.ultimaFecha ?? '') < (b.ultimaFecha ?? '') ? 1 : -1);
  }, [datos.ejercicios, datos.contextos, formula]);

  const balances = useMemo<{ empujeTiron: Balance; superiorInferior: Balance }>(
    () => ({
      empujeTiron: balanceEmpujeTiron(datos.contextos),
      superiorInferior: balanceSuperiorInferior(datos.contextos),
    }),
    [datos.contextos],
  );

  /**
   * Por qué las dos cuentas no dan el mismo total.
   *
   * "11 empuje · 9 tirón" (20) arriba de "23 superior · 0 pierna" (23) se lee como un error de la
   * app. No lo es: son dos clasificaciones distintas sobre las mismas series, y una serie que no
   * es ni empuje ni tirón (el core, por ejemplo) no entra en ese balance. Sin decirlo, el usuario
   * queda eligiendo a cuál de los dos números creerle.
   */
  const notaBalances = useMemo<string | null>(() => {
    const total = seriesEfectivas(datos.contextos).length;
    if (total === 0) return null;

    const sinPatron = total - (balances.empujeTiron.a + balances.empujeTiron.b);
    const sinRegion = total - (balances.superiorInferior.a + balances.superiorInferior.b);
    if (sinPatron <= 0 && sinRegion <= 0) return null;

    const partes: string[] = [];
    if (sinPatron > 0) {
      partes.push(`${sinPatron} ${sinPatron === 1 ? 'no es' : 'no son'} ni empuje ni tirón`);
    }
    if (sinRegion > 0) {
      partes.push(`${sinRegion} ${sinRegion === 1 ? 'no es' : 'no son'} ni superior ni pierna`);
    }
    return `Los dos balances no suman lo mismo: de ${total} series, ${partes.join(' y ')}. Cada uno compara sólo lo que le toca; lo que no encaja queda afuera.`;
  }, [datos.contextos, balances]);

  const calor = useMemo<CeldaMapaCalor[]>(
    () =>
      mapaCalor(datos.contextos, datos.hoy, {
        dias: 7,
        objetivoSeriesSemanales: datos.perfil?.objetivo_series_semanales ?? {},
      }),
    [datos.contextos, datos.hoy, datos.perfil],
  );

  const frecuencia = useMemo<{ musculo: Musculo; veces: number }[]>(() => {
    const mapa = frecuenciaPorMusculo(datos.contextos, datos.hoy, 28);
    return [...mapa.entries()]
      .map(([musculo, veces]) => ({ musculo, veces }))
      .sort((a, b) => b.veces - a.veces);
  }, [datos.contextos, datos.hoy]);

  /**
   * Cómo se lee la lista de frecuencia. Sin esta línea, "1,5×" no dice qué se contó: son días
   * distintos, no series, y esa es justo la confusión que hace parecer alto un número bajo.
   */
  const lecturaFrecuencia = useMemo<string | null>(() => {
    const mas = frecuencia[0];
    if (!mas) return null;
    const base = 'Son días distintos, no series: cuatro ejercicios de pecho el mismo día cuentan una vez.';
    return `${base} En 28 días entraron ${frecuencia.length} ${
      frecuencia.length === 1 ? 'músculo' : 'músculos'
    }; el que más se repite es ${nombreMusculo(mas.musculo)}, ${formatearNumero(mas.veces, 1)}× por semana.`;
  }, [frecuencia]);

  const cardio = useMemo<{ porNivel: TendenciaNivel[]; sinNivel: number }>(() => {
    const fechaPorSesion = new Map(datos.sesiones.map((s) => [s.id, s.fecha]));
    const contextos = datos.cardio
      .map((registro) => {
        const fecha = fechaPorSesion.get(registro.sesion_id);
        return fecha ? { registro, fecha } : null;
      })
      .filter((x): x is { registro: (typeof datos.cardio)[number]; fecha: string } => x !== null);

    const r = tendenciaPorNivel(contextos);
    return { porNivel: r.porNivel, sinNivel: r.sinNivel.length };
  }, [datos.cardio, datos.sesiones]);

  /** El que el usuario abrió. null = todavía está mirando la lista (es lo que usa la mobile). */
  const abierto = elegido !== null && filas.some((f) => f.ejercicio.id === elegido) ? elegido : null;
  const detalle = filas.find((f) => f.ejercicio.id === abierto) ?? filas[0] ?? null;

  // Se pidió un ejercicio sin una sola serie completada. Si no se dice, la pantalla abre otro y
  // parece que el enlace no funcionó.
  const pedidoSinDatos =
    elegido !== null && abierto === null ? datos.ejerciciosPorId.get(elegido) ?? null : null;

  return {
    datos,
    filas,
    abierto,
    abrir: setElegido,
    pedidoSinDatos,
    seleccionado: detalle?.ejercicio.id ?? null,
    setSeleccionado: setElegido,
    detalle,
    balances,
    notaBalances,
    calor,
    frecuencia,
    lecturaFrecuencia,
    cardio,
    formula,
    advertenciaE1rm: E1RM_ADVERTENCIA,
    /** Columnas de la tabla desktop que sobran cuando ninguna fila las llena. */
    hayDeltaE1rm: filas.some((f) => f.deltaE1rm !== null),
    hayEstancamiento: filas.some((f) => f.estancamiento !== null),
  };
}
